if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.timeout;
        this.form = this.querySelector('form');

        // Guard: if no form is present, do not initialize
        if (!this.form) return;

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.quantityInput = this.querySelector('.quantity__input');
        this.quickModal = this.closest('.quick-add');
        this.hideErrors = this.dataset.hideErrors === 'true';

        if (this.quantityInput) {
          this.quantityInput.addEventListener('error', this.validateQuantity.bind(this));
        }

        this.addToCartButtons = this.querySelectorAll('[data-add-to-cart]');
        if (document.querySelector('cart-drawer')) {
          this.addToCartButtons.forEach((submitButton) => {
            submitButton.setAttribute('aria-haspopup', 'dialog');
          });
        }

        // Fix debounce wiring
        this.onWindowResize = this.debounce(() => this.handleWindowResize());
        this.productInfoWrapper = this.closest('.product__info-wrapper');

        this.addToCartSection = document.querySelector('.product-single__meta');

        if (this.addToCartSection) {
          this.addToCartContent = this.addToCartSection.querySelector('.product__meta-grid');
          this.productSelectors = this.addToCartContent
            ? this.addToCartContent.querySelector('.product__meta-grid-item--selectors')
            : null;
          this.lowStockNotification = this.addToCartContent
            ? this.addToCartContent.querySelector('[data-left-quantity]')
            : null;
        }

        this.controlsGroupSubmitTrigger = this.querySelector('#data-controls-group-submit-trigger');
        this.controlsGroupSubmit = this.querySelector('[data-controls-group-submit]');
        this.goToCartButtons = this.querySelectorAll('[data-go-to-cart]');

        this.dynamicCheckoutWrap = this.querySelector('.product-form__dynamic-buttons');
        if (this.dynamicCheckoutWrap) {
          this.dynamicCheckoutWrap.addEventListener('click', (evt) => {
            // Only block click if dynamic buttons are disabled until required options are valid
            if (this.dynamicCheckoutWrap.classList.contains('product-form__dynamic-buttons--disable')) {
              evt.preventDefault();

              if (!this.validateCustomProperties()) {
                this.handleErrorMessage(`${window.variantStrings.customPropertyError}`);
                this.handleButtonLoadingState(false);
              } else {
                this.dynamicCheckoutWrap.classList.remove('product-form__dynamic-buttons--disable');
              }
              return;
            }

            // If enabled, allow normal payment-button behavior. Just add loading UI politely.
            this.dynamicCheckoutWrap.classList.add('product-form__dynamic-buttons--loading');

            const unbrandedSelector = '.shopify-payment-button__button--unbranded';
            let shopifyButton = evt.target;

            if (shopifyButton && shopifyButton.nodeType === 1) {
              if (!shopifyButton.classList.contains('shopify-payment-button__button--unbranded')) {
                shopifyButton = shopifyButton.closest(unbrandedSelector);
              }
            } else {
              shopifyButton = null;
            }

            if (shopifyButton && !shopifyButton.dataset.loading) {
              shopifyButton.innerHTML = `<span class="product-form-loader" data-loader>
                <div class="loader-bounce">
                  <div class="bounce1"></div>
                  <div class="bounce2"></div>
                  <div class="bounce3"></div>
                </div>
              </span>`;
              shopifyButton.dataset.loading = 'true';
            }
          });
        }

        if (!this.quickModal) {
          this.onScrollHandler = this.onScroll.bind(this);
        }

        window.addEventListener('resize', this.onWindowResize);

        // Guard enquire usage (prevents hard crash if library not present)
        this.initBreakpoints();
      }

      initBreakpoints() {
        if (typeof window.enquire === 'undefined') return;

        const self = this;

        window.enquire.register('screen and (max-width: 1000px)', {
          match: () => {
            window.addEventListener('scroll', self.onScrollHandler, false);
            self.bpSmall = true;
          },
          unmatch: () => {
            window.removeEventListener('scroll', self.onScrollHandler, false);
            self.bpSmall = false;
          },
        });
      }

      onScroll() {
        if (!this.controlsGroupSubmitTrigger || !this.controlsGroupSubmit) return;

        const elemTop = this.controlsGroupSubmitTrigger.getBoundingClientRect().top;
        const controlsIsFixed = this.controlsGroupSubmit.classList.contains('fix-controls-group--submit');

        if (controlsIsFixed && elemTop > -56) {
          requestAnimationFrame(this.resetAddToCartButton.bind(this));
        } else if (!controlsIsFixed && elemTop < -56) {
          requestAnimationFrame(this.stickyAddToCartButton.bind(this));
        }
      }

      resetAddToCartButton() {
        if (!this.controlsGroupSubmit) return;
        this.controlsGroupSubmit.classList.remove('fix-controls-group--submit');
      }

      stickyAddToCartButton() {
        if (!this.controlsGroupSubmit) return;
        this.controlsGroupSubmit.classList.add('fix-controls-group--submit');
      }

      handleWindowResize() {
        if (!this.addToCartSection) return;
        const addToCartHeight = this.addToCartSection.offsetHeight;
        this.addToCartSection.style.minHeight = `${addToCartHeight}px`;
      }

      debounce(func, timeout = 300) {
        let timer;
        return (...args) => {
          clearTimeout(timer);
          timer = setTimeout(() => {
            func.apply(this, args);
          }, timeout);
        };
      }

      validateQuantity(evt) {
        evt.preventDefault();

        if (!this.quantityInput) return;

        const quantityLimit = this.quantityInput.getAttribute('max');
        const lowStockIsInactive = !this.lowStockNotification || this.lowStockNotification.textContent === '';

        if (!quantityLimit) return;

        if (lowStockIsInactive) {
          this.handleErrorMessage(
            window.variantStrings.lowStockError.replace('[quantity]', quantityLimit)
          );
        }
      }

      getValidationError() {
        let error = '';

        if (!this.productSelectors) return error;

        let fieldsetsRadios = Array.from(this.productSelectors.querySelectorAll('fieldset')) || [];
        let selects = Array.from(this.productSelectors.querySelectorAll('select')) || [];

        if (selects.length) {
          selects = selects.map((select) => {
            const nameRegex = (select.dataset.name || '').match(/\[(.*?)\]/) || [];
            const name = nameRegex[1] || '';
            const hasValue = typeof select.value !== 'undefined' && `${select.value}`.trim() !== '';
            return hasValue ? { name, value: select.value } : { name };
          });
        }

        if (fieldsetsRadios.length) {
          fieldsetsRadios = fieldsetsRadios.map((fieldset) => {
            const inputChecked = Array.from(fieldset.querySelectorAll('input')).find((radio) => radio.checked);
            const anyInput = fieldset.querySelector('input');
            const name = anyInput ? anyInput.dataset.name : '';

            if (inputChecked) {
              return { name: inputChecked.dataset.name, value: inputChecked.value };
            }
            return { name };
          });
        }

        const options = fieldsetsRadios.concat(selects);
        options.forEach((option) => {
          if (!option.value && option.name) {
            error = `${window.variantStrings.variantValidError} ${option.name.toLocaleLowerCase()}`;
          }
        });

        return error;
      }

      validateCustomProperties() {
        const productMetagrid = this.form.closest('.product__meta-grid');
        const customInputs = productMetagrid
          ? Array.from(productMetagrid.querySelectorAll(`.product_custom-options [aria-required]`))
          : [];

        if (customInputs.length === 0) return true;

        const results = customInputs.map((customInput) => {
          // If required, validate. If not required, treat as valid.
          if (customInput && customInput.required) {
            return !!validateFormInput(customInput);
          }
          return true;
        });

        return results.every((v) => v === true);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        this.handleErrorMessage();
        this.handleButtonLoadingState(true);

        if (!this.validateCustomProperties()) {
          this.handleErrorMessage(`${window.variantStrings.customPropertyError}`, true);
          this.handleButtonLoadingState(false);
          return false;
        }

        // Defensive: make sure quantity never submits blank/0
        if (this.quantityInput) {
          const q = parseInt(this.quantityInput.value, 10);
          if (!q || q < 1) this.quantityInput.value = '1';
        }

        let cartsections = [];
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          cartsections = this.cart.getSectionsToRender().map((section) => section.id);
          formData.append('sections', cartsections);
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}.js`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.description,
                message: response.message,
              });

              let errorMsg = response.description;

              if (!this.hideErrors) {
                const r = /All \d+/;
                const matchItemsLeft = errorMsg.match(r);
                const r2 = /You can't add more /i;
                const matchCantAdd = errorMsg.match(r2);
                const r3 = /is already sold out/i;
                const matchSoldOut = errorMsg.match(r3);

                if (matchItemsLeft && matchItemsLeft.length) {
                  const itemsLeft = matchItemsLeft[0].replace('All ', '');
                  errorMsg = `We only have ${itemsLeft} and they’re already in your cart!`;
                  if (itemsLeft === '1') {
                    errorMsg = `We only have ${itemsLeft} and it's already in your cart!`;
                  }
                } else if (matchSoldOut && matchSoldOut.length) {
                  errorMsg = `This product is already sold out.`;
                }

                if (matchCantAdd && matchCantAdd.length) {
                  errorMsg = `You can't add more of this item to your cart.`;
                }
              }

              this.handleButtonLoadingState(false);
              this.handleErrorMessage(errorMsg);
              return false;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return false;
            }

            const qtyValue = this.quantityInput ? this.quantityInput.value : 1;

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
            });

            if (!response.sections) {
              return fetch(`?sections=${cartsections}`);
            } else {
              this.handleCartUpdate(response, qtyValue);
              return false;
            }
          })
          .then((response) => response && response.json())
          .then((response) => {
            if (!response) return;

            const qtyValue = this.quantityInput ? this.quantityInput.value : 1;

            const formatResponse = {};
            formatResponse.sections = response;
            this.handleCartUpdate(formatResponse, qtyValue);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            if (!this.bpSmall || (this.cart && this.cart.classList.contains('drawer'))) {
              this.handleButtonLoadingState(false);
            }
          });
      }

      handleCartUpdate(response, qtyValue) {
        const quickAddModal = this.closest('quick-add-modal');
        let hideQuickAddModal = !!quickAddModal;
        const isCartDrawer = this.cart && this.cart.classList.contains('drawer');

        if (this.bpSmall && !isCartDrawer) {
          hideQuickAddModal = false;
        }

        if (hideQuickAddModal) {
          document.body.addEventListener(
            'modalClosed',
            () => {
              setTimeout(() => {
                this.cart.renderContents(response, qtyValue);
              });
            },
            { once: true }
          );
          quickAddModal.hide(true);
        } else if (this.bpSmall && !isCartDrawer) {
          this.renderBubbleCart(response);
          this.showGoToCartButton();
        } else {
          this.cart.renderContents(response, qtyValue);
        }
      }

      renderBubbleCart(response) {
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble) {
          const html = response.sections['cart-icon-bubble'];
          const sourceCartIcon = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
          cartIconBubble.innerHTML = sourceCartIcon.innerHTML;
        }
      }

      showGoToCartButton() {
        if (this.quickModal) this.quickModal.classList.add('quick-add--visible');

        this.goToCartButtons.forEach((goToCartButton) => {
          goToCartButton.classList.add('product-form__open-cart--fadein');
        });

        setTimeout(() => {
          this.handleButtonLoadingState(false);
        }, 500);

        setTimeout(() => {
          if (this.quickModal) this.quickModal.classList.remove('quick-add--visible');
          this.goToCartButtons.forEach((goToCartButton) => {
            goToCartButton.classList.remove('product-form__open-cart--fadein');
          });
        }, 5000);
      }

      handleButtonLoadingState(isLoading) {
        if (isLoading) {
          this.addToCartButtons.forEach((addToCart) => {
            addToCart.disabled = true;
            addToCart.setAttribute('aria-disabled', true);
            addToCart.classList.add('product-form__cart-submit--loading', 'btn--loading');
          });
          if (this.quickModal) this.quickModal.classList.add('quick-add--visible');
        } else {
          let activeButton = false;

          this.addToCartButtons.forEach((addToCart) => {
            addToCart.disabled = false;
            addToCart.removeAttribute('aria-disabled');
            addToCart.classList.remove('product-form__cart-submit--loading', 'btn--loading');
            if (addToCart.classList.contains('btn--load-error')) activeButton = true;
          });

          this.goToCartButtons.forEach((goToCartButton) => {
            if (goToCartButton.classList.contains('product-form__open-cart--fadein')) activeButton = true;
          });

          if (this.quickModal && !activeButton) this.quickModal.classList.remove('quick-add--visible');
        }
      }

      handleErrorMessage(errorMessage = false, linkToError) {
        if (this.hideErrors) return;

        this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;

        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');
        this.errorButtonFixed =
          this.errorButtonFixed || this.querySelector('.product-form__error-message-wrapper.mobile--error');
        this.errorMessageFixed = this.errorButtonFixed
          ? this.errorButtonFixed.querySelector('.product-form__error-message')
          : null;

        if (errorMessage) {
          this.errorMessageWrapper.classList.remove('hidden');
          if (this.errorButtonFixed) {
            this.errorButtonFixed.classList.remove('mobile--error--hidden');
          }

          this.errorMessage.textContent = errorMessage;
          if (this.errorMessageFixed) {
            this.errorMessageFixed.textContent = errorMessage;
          }

          if (linkToError) {
            this.errorMessage.textContent = '';
            const iconStyle = this.errorMessage.dataset.iconStyle;
            const linkElm = document.createElement('a');
            linkElm.setAttribute('class', 'product-form__error-message__link');
            linkElm.setAttribute('href', '#top');
            linkElm.textContent = ` ${errorMessage}`;
            const icon = document.createElement('i');
            icon.setAttribute('class', `icon ${iconStyle} fa-wrench`);
            this.errorMessage.appendChild(linkElm);

            linkElm.addEventListener(
              'click',
              (evt) => {
                evt.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              },
              false
            );
          }
        } else {
          this.errorMessageWrapper.classList.add('hidden');
          if (this.errorButtonFixed) {
            this.errorButtonFixed.classList.add('mobile--error--hidden');
          }
        }
      }
    }
  );
}
